/**
 * The phone game layout (M1b, functional only): board on top, Rail underneath,
 * in ONE column that does not scroll — the Rail's own body is the scroller, so
 * the toolbar stays pinned at the bottom of the screen and the event dock sits
 * directly above it. The draggable Sheet and the gestures are a later
 * milestone.
 *
 * Why not a ScrollView around both: inside a scroller `flex: 1` collapses, the
 * Rail would grow to its content height and the toolbar would be reachable only
 * by scrolling past the notation — the one control that must never move.
 *
 * A dumb slot placer (unit convention §4): no store reads.
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

import {SPACE} from '../theme/tokens';

export type PhoneLayoutProps = {
  stage: React.ReactNode;
  /** a SIBLING of the stage, so the board's DOM is never re-parented */
  overlay: React.ReactNode;
  rail: React.ReactNode;
};

/**
 * The rail keeps a usable height even on a short viewport (a 390×844 phone
 * leaves it ~330 px after the board): header + one dock card + the toolbar.
 */
const RAIL_MIN_HEIGHT = 240;

export default React.memo(function PhoneLayout({
  stage,
  overlay,
  rail,
}: PhoneLayoutProps) {
  return (
    <View style={styles.column}>
      <View style={styles.boardColumn}>
        {stage}
        {overlay}
      </View>
      <View style={styles.rail}>{rail}</View>
    </View>
  );
});

const styles = StyleSheet.create({
  column: {
    flex: 1,
    padding: SPACE.sm,
    gap: SPACE.sm,
  },
  boardColumn: {
    position: 'relative',
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'center',
  },
  rail: {
    flex: 1,
    // a flex child must be allowed to shrink below its content, or the Rail's
    // own ScrollView never gets a bounded height
    minHeight: RAIL_MIN_HEIGHT,
  },
});
